import { forwardRef, useImperativeHandle, type Ref } from 'react';
import CampoArea from '@/components/base/CampoArea';
import BlocoRecebedor from './BlocoRecebedor';
import type { EventoDraft, ModoPagamento } from '../types';

export interface PagamentoLegadoHandle {
  validar: () => boolean;
}

interface PagamentoLegadoProps {
  eventoId: number | null;
  draft: EventoDraft;
  onChange: (patch: Partial<EventoDraft>) => void;
}

const OPCOES: {
  valor: ModoPagamento;
  icone: string;
  titulo: string;
  descricao: string;
}[] = [
  {
    valor: 'plataforma',
    icone: 'ri-bank-card-line',
    titulo: 'Pix pela plataforma',
    descricao:
      'A pessoa paga pelo app, na hora da inscrição. O dinheiro entra pela plataforma e é repassado para a chave Pix que você indicar.',
  },
  {
    valor: 'rifa',
    icone: 'ri-ticket-2-line',
    titulo: 'Ação entre amigos',
    descricao:
      'A inscrição é paga vendendo números de uma ação entre amigos. Cada inscrito ganha um talão. Quem não vender tudo compra os números que faltam — e concorre aos prêmios do mesmo jeito.',
  },
  {
    valor: 'gratuito',
    icone: 'ri-gift-line',
    titulo: 'Gratuito',
    descricao: 'Não se cobra nada. A pessoa só preenche a ficha.',
  },
  {
    valor: 'direto',
    icone: 'ri-hand-heart-line',
    titulo: 'Direto com a organização',
    descricao:
      'A plataforma divulga e recolhe as fichas. O pagamento é combinado fora — dinheiro, Pix da paróquia, o que vocês já fazem hoje.',
  },
];

function PagamentoLegado(
  { eventoId, draft, onChange }: PagamentoLegadoProps,
  ref: Ref<PagamentoLegadoHandle>,
) {
  useImperativeHandle(ref, () => ({
    validar() {
      return true;
    },
  }));

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-normal text-foreground-950">Como se paga a inscrição</h2>
        <p className="mt-1 text-sm text-secondary-500">
          Escolha um jeito. Dá para mudar depois, enquanto o evento estiver em rascunho.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {OPCOES.map((o) => {
          const ativo = draft.modoPagamento === o.valor;
          return (
            <button
              key={o.valor}
              type="button"
              onClick={() => onChange({ modoPagamento: o.valor })}
              className={`cursor-pointer rounded-lg border p-4 text-left transition-colors ${
                ativo
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-secondary-200 bg-white hover:border-primary-300'
              }`}
            >
              <span
                className={`flex h-10 w-10 items-center justify-center rounded-full ${
                  ativo ? 'bg-primary-500 text-white' : 'bg-primary-100 text-primary-700'
                }`}
              >
                <i className={`${o.icone} text-xl`} />
              </span>
              <span className="mt-3 block text-[15px] font-semibold text-foreground-950">
                {o.titulo}
              </span>
              <span className="mt-1 block text-sm text-secondary-600">{o.descricao}</span>
            </button>
          );
        })}
      </div>

      {draft.modoPagamento === 'plataforma' && (
        <div className="space-y-5">
          <BlocoRecebedor eventoId={eventoId} />
        </div>
      )}

      {draft.modoPagamento === 'rifa' && (
        <div className="flex items-start gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-primary-600">
            <i className="ri-information-line text-lg" />
          </span>
          <p className="text-sm text-foreground-800">
            No próximo passo você monta a ação entre amigos deste evento: os prêmios, o valor de cada
            número e a ação.
          </p>
        </div>
      )}

      {draft.modoPagamento === 'gratuito' && (
        <div className="flex items-start gap-3 rounded-lg border border-secondary-200 bg-white px-4 py-3">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-secondary-500">
            <i className="ri-information-line text-lg" />
          </span>
          <p className="text-sm text-foreground-800">Ninguém paga nada. É só preencher a ficha.</p>
        </div>
      )}

      {draft.modoPagamento === 'direto' && (
        <div className="rounded-lg border border-secondary-200 bg-white p-5">
          <CampoArea
            id="como-pagar-direto"
            name="como_pagar"
            label="O que dizer para quem quiser pagar"
            help="Aparece para quem se inscreveu, explicando como acertar o pagamento com a organização."
            value={draft.textoPagamento}
            onChange={(v) => onChange({ textoPagamento: v })}
            placeholder="Procure a secretaria da paróquia de terça a sexta, das 14h às 17h."
          />
        </div>
      )}
    </div>
  );
}

export default forwardRef(PagamentoLegado);